import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  salesOrdersApi,
  type SalesOrderStatus,
  type SalesOrderSummary
} from '@/api/modules/salesOrders';
import { PaginatedList } from '@/components/data/PaginatedList';
import { paths } from '@/router/paths';

const STATUS_COLORS: Record<SalesOrderStatus, 'default' | 'primary' | 'info' | 'success' | 'error' | 'warning'> = {
  DRAFT: 'default',
  CONFIRMED: 'primary',
  IN_PRODUCTION: 'info',
  SHIPPED: 'warning',
  DELIVERED: 'success',
  CANCELLED: 'error'
};

export function SalesOrderListPage() {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: ['sales-orders', 'summaries', page],
    queryFn: () => salesOrdersApi.summaries(page, 20)
  });

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" gutterBottom>
            Sales orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Confirmed customer orders. Tap a row for detail and actions.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(paths.salesOrderCreate)}
        >
          New sales order
        </Button>
      </Stack>

      <PaginatedList<SalesOrderSummary>
        page={query.data}
        isLoading={query.isLoading}
        error={query.error}
        onPageChange={setPage}
        renderItem={(so) => (
          <Card variant="outlined">
            <CardActionArea
              onClick={() =>
                navigate(paths.salesOrderDetail.replace(':id', so.id))
              }
            >
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h3">{so.code}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Customer: {so.customerCode}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {so.currency} {so.totalAmount}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={so.status.replace('_', ' ')}
                    color={STATUS_COLORS[so.status]}
                  />
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        )}
        emptyMessage="No sales orders yet. Click New sales order to create one."
      />
    </Box>
  );
}
